import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, Loader, NameHint } from "@/components/ui-bits";
import { useNameLookup } from "@/hooks/useNameLookup";
import { Check, Loader2, Package, Sparkles, Lock, User, Users } from "lucide-react";

export default function Packages() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [joinFor, setJoinFor] = useState("self");
  const [downlineCode, setDownlineCode] = useState("");
  const downlineLookup = useNameLookup(joinFor === "downline" ? downlineCode : "");

  const load = () => API.get("/packages").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    if (joinFor === "downline" && !downlineCode.trim()) {
      return toast.error("Enter the downline User ID");
    }
    setBusy(id);
    try {
      const payload = { package_id: id };
      if (joinFor === "downline") payload.target_referral_code = downlineCode.trim();
      const r = await API.post("/packages/activate", payload);
      toast.success(r.data.message);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <Loader label="Loading packages" />;

  const req = data.retopup_required || {};
  const rc = data.retopup_count || {};
  const maxes = data.package_max_activations || { "1": 2, "2": 2, "3": 0 };
  // Effective activation counts: downline's counts when joining for a downline, else self.
  const effRc = joinFor === "downline"
    ? (downlineLookup.status === "found" ? (downlineLookup.data?.retopup_count || {}) : {})
    : rc;
  // Highest package already activated (for no-downgrade rule).
  const highest = Math.max(0, ...Object.keys(effRc).filter((k) => (effRc[k] || 0) > 0).map(Number));
  const currentIds = (data.packages || []).map((p) => p.id);
  const maxAvailable = Math.max(0, ...currentIds);
  // Member whose top package was removed (legacy Elite) — may re-top the top available tier.
  const isLegacy = highest > 0 && !currentIds.includes(highest);
  // Sequential unlock + lifetime max + no downgrade (applies to BOTH self and downline).
  const lockInfo = (id) => {
    if (joinFor === "downline" && downlineLookup.status !== "found")
      return { locked: true, reason: "Select a valid downline" };
    if (id === 2 && (effRc["1"] || 0) < (req["1"] || 2))
      return { locked: true, reason: `Needs Starter ×${req["1"] || 2}` };
    if (id === 3 && (effRc["2"] || 0) < (req["2"] || 2))
      return { locked: true, reason: `Needs Pro ×${req["2"] || 2}` };
    if (id < Math.min(highest, maxAvailable))
      return { locked: true, reason: "Can't downgrade" };
    const mx = maxes[String(id)] || 0;
    if (mx && (effRc[String(id)] || 0) >= mx && !(isLegacy && id === maxAvailable))
      return { locked: true, reason: `Max reached (${mx}/${mx})` };
    return { locked: false, reason: "" };
  };

  return (
    <div>
      <PageHeader title="Join / Upgrade" subtitle="Activate or re-top-up a package. Up to 40% of the cost comes from your Top-Up wallet; the rest from your Deposit wallet." testid="packages-header" />

      {/* Self / Downline selector */}
      <div className="glass rounded-2xl p-4 mb-6" data-testid="join-for-selector">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Joining for</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2">
            <button data-testid="join-self" onClick={() => setJoinFor("self")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                joinFor === "self" ? "bg-emerald-500 text-slate-900" : "bg-white/5 text-slate-400 hover:text-white"}`}>
              <User className="h-4 w-4" /> Myself
            </button>
            <button data-testid="join-downline" onClick={() => setJoinFor("downline")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                joinFor === "downline" ? "bg-emerald-500 text-slate-900" : "bg-white/5 text-slate-400 hover:text-white"}`}>
              <Users className="h-4 w-4" /> A Downline
            </button>
          </div>
          {joinFor === "downline" && (
            <div className="flex-1">
              <input data-testid="downline-code" value={downlineCode} onChange={(e) => setDownlineCode(e.target.value)}
                placeholder="Downline User ID (e.g. SO1A2B3C4D)"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none focus:border-emerald-500/50 font-num" />
              <NameHint state={downlineLookup} testid="downline-name" />
            </div>
          )}
        </div>
        {joinFor === "downline" && <p className="text-xs text-slate-500 mt-2">Cost is paid from YOUR wallets; the package activates on your downline's account.</p>}
      </div>

      <div className="glass rounded-2xl p-4 mb-6 text-sm text-slate-400 flex flex-wrap gap-x-8 gap-y-2">
        <span>{joinFor === "downline" ? "Downline's activations" : "Your activations"} — <span className="text-white font-num">Starter: {effRc["1"] || 0}</span></span>
        <span className="text-white font-num">Pro: {effRc["2"] || 0}</span>
        <span className="text-slate-500">Package 2 (Pro) unlocks after Starter ×{req["1"] || 2}. Starter can be activated {maxes["1"] || 2}× (lifetime); Pro can be re-topped up unlimited times to refill your earning cap. No downgrades.</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
        {data.packages.map((p) => {
          const total = p.amount + (p.silver_card || 0);
          const cap = p.amount * p.cap_multiplier;
          const isCurrent = joinFor === "self" && data.current_package === p.id;
          const info = lockInfo(p.id);
          const locked = info.locked;
          const lockMsg = info.reason;
          return (
            <div key={p.id} data-testid={`package-card-${p.id}`}
              className={`glass rounded-2xl p-6 card-hover relative ${p.id === 2 ? "border-cyan-500/30" : ""}`}>
              {p.id === 2 && <span className="absolute top-4 right-4 text-[0.6rem] uppercase tracking-wide bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-2 py-0.5 rounded-full">Premium</span>}
              <div className="flex items-center gap-2 text-emerald-400 mb-3"><Package className="h-5 w-5" /><span className="font-display text-xl font-bold text-white">{p.name}</span></div>
              <div className="font-num text-4xl font-black text-white">{fmt(total)}</div>
              {p.silver_card > 0 && <div className="text-xs text-slate-500 mt-1">${p.amount} + ${p.silver_card} Silver Card</div>}
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2 text-slate-300"><Check className="h-4 w-4 text-emerald-400" /> Earning cap {p.cap_multiplier}× = <span className="font-num text-white">{fmt(cap)}</span></li>
                <li className="flex items-center gap-2 text-slate-300"><Sparkles className="h-4 w-4 text-violet-400" /> {p.magic_cards} Magic Cards</li>
              </ul>
              <button
                data-testid={`activate-package-${p.id}`}
                disabled={busy === p.id || locked}
                onClick={() => activate(p.id)}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-semibold transition-colors ${
                  locked ? "bg-white/5 text-slate-500 cursor-not-allowed"
                  : "bg-emerald-500 hover:bg-emerald-400 text-slate-900"}`}>
                {busy === p.id && <Loader2 className="h-4 w-4 animate-spin" />}
                {locked ? <><Lock className="h-4 w-4" /> {lockMsg}</> : (isCurrent || (effRc[String(p.id)] || 0) > 0 || (isLegacy && p.id === maxAvailable)) ? "Re-Top-Up" : "Activate"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
